import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, DynamicIconWrapper, Icons } from '@components/common';
import Actions from '@components/common/Actions';
import NotesTableCell from '@components/common/NotesTableCell';

import { IOperationWindowRule, IWorkingDays } from '@interfaces/cmModule/operationWindowRules';

import { defaultOperationRulesSchedule } from './OperationWindowRulesForm/constants';

import styles from './OperationWindowRules.module.scss';

export const getWorkingDaysSchedule = (data: IOperationWindowRule) => {
	const days: IWorkingDays[] = data.workingDays.map((day) =>({
			...day,
			categoryName: day.weekDay.name,
			ranges: day.ranges.map((range) => ({
				id: `${range.from} - ${range.to}`,
				from: range.from,
				to: range.to,
			})),
		}),
	);

	return defaultOperationRulesSchedule.map((day) => {
		const foundDay = days
			.find((item) => day.categoryName === item.categoryName);

		if (foundDay) {
			return foundDay;
		}

		return day;
	}, []);
};

interface IGetOperationRulesColumns {
	isViewerOnly?: boolean;
	openNotesModal: (isEdit: boolean) => void;
	setSelectedOperationRule: (rule?: IOperationWindowRule) => void;
	openOperationRuleModal: () => void;
	openDeleteTemplateModal: () => void;
	setIsReadOnlyRules: (isReadOnly: boolean) => void;
}

export const getOperationRulesColumns = ({
	isViewerOnly = false,
	openNotesModal,
	setSelectedOperationRule,
	openOperationRuleModal,
	openDeleteTemplateModal,
	setIsReadOnlyRules,
}: IGetOperationRulesColumns) => ([
	{
		header: 'Name',
		accessorKey: 'name',
		enableSorting: true,
		cell: ({ getValue }: CellContext<IOperationWindowRule, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Working Days',
		accessorKey: 'workingDays',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IOperationWindowRule, any>) => {
			const { workingDays } = original || {};

			const isWorkingDaysEmpty = !workingDays || workingDays?.filter((day) => day.weekDay).length === 0;

			const days = workingDays?.filter((day) => day.weekDay).map((day) => day.weekDay?.shortTitle).join(', ') || '-';

			const handleOpenOperationRuleModal = () => {
				setSelectedOperationRule(original);
				setIsReadOnlyRules(true);
				openOperationRuleModal();
			};

			return (
				<AdaptiveTableCell
					actionHandler={!isWorkingDaysEmpty ? handleOpenOperationRuleModal : undefined}
					text={days}
				/>
			);
		},
	},
	{
		header: 'Assigned',
		accessorKey: 'assignedCount',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IOperationWindowRule, any>) => {
			const assignedCount = getValue();

			return assignedCount ? (
				<div className={styles.assignedCountWrapper}>
					<span className={styles.assignedCount}>{`View (${assignedCount})`}</span>
					<DynamicIconWrapper
						className={styles.assignedCountIcon}
						icon={Icons.viewIcon}
					/>
				</div>
			) : <AdaptiveTableCell text='N/A' />;
		},
	},
	{
		header: 'Notes',
		accessorKey: 'note',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IOperationWindowRule, any>) => {
			const { note } = original || {};

			const handleOpenNotes = (isEdit: boolean) => () => {
				openNotesModal(isEdit);
				setSelectedOperationRule(original);
			};

			const isNoteExist = !!note;

			return (
				<NotesTableCell
					handleOpenNotes={handleOpenNotes}
					isNoteExist={isNoteExist}
					isReadOnly={isViewerOnly}
				/>
			);
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		isHidden: isViewerOnly,
		cell: ({ row: { original } }: CellContext<IOperationWindowRule, any>) => {
			const editHandler = () => {
				setSelectedOperationRule(original);
				openOperationRuleModal();
			};

			const handleDelete = () => {
				setSelectedOperationRule(original);
				openDeleteTemplateModal();
			};

			return (
				<Actions
					deleteTooltip='Delete template'
					editHandler={editHandler}
					editTooltip='Edit details'
					handleDelete={handleDelete}
					isDelete
					isEdit
					viewTooltip='View tree'
				/>
			);
		},
	},
]);
